import { useCallback } from 'react';

import { useLocalStorageState } from '@components/component-helpers';
import { uuid } from '@utils/uuid';

import { useAIBuilderMessages } from 'api/ai-builder/hooks';

export type ChatMessageRole = 'user' | 'assistant' | 'tool';
export type ToolStatus = 'in_progress' | 'done' | 'error';

type BaseMessage = {
  id: string;
  role: ChatMessageRole;
  text: string;
  errorType?: string;
  errorMessage?: string;
};

type ToolMessage = Omit<BaseMessage, 'text'> & {
  role: 'tool';
  callId: string;
  toolName?: string;
  toolLabel?: string;
  toolDescription?: string;
  status: ToolStatus;
  text?: string;
};

type UserMessage = BaseMessage & { role: 'user'; files?: string[] };
type AssistantMessage = BaseMessage & { role: 'assistant' };

export type ChatMessage = UserMessage | AssistantMessage | ToolMessage;

type ToolCallBase = {
  type: 'tool_call';
  callId: string;
  name?: string;
  toolType?: 'create_action' | 'edit_action' | 'delete_action' | 'helper' | 'move_action';
  label?: string;
  description?: string;
};

type ToolCallStart = ToolCallBase & {
  event: 'start';
};

type ToolCallDelta = ToolCallBase & {
  event: 'delta';
  content?: string;
};

type ToolCallEnd = ToolCallBase & {
  event: 'end';
  hasError?: boolean;
  errorType?: string;
  content?: string;
};

type ToolCallAllDone = ToolCallBase & {
  event: 'task_complete';
};

type ToolCallEvent = ToolCallStart | ToolCallDelta | ToolCallEnd | ToolCallAllDone;

type StatusBaseEvent = {
  type: 'status';
  event: 'error' | 'interrupted' | 'completed';
};

type ErrorEvent = StatusBaseEvent & {
  event: 'error';
  messageId?: string;
  message?: string;
  errorType?: string;
};

type InterruptedEvent = StatusBaseEvent & {
  event: 'interrupted';
};

type StatusEvent = ErrorEvent | InterruptedEvent;

type AssistantMessageStart = {
  type: 'message';
  event: 'start';
  messageId: string;
  content?: string;
};

type AssistantMessageDelta = {
  type: 'message';
  event: 'delta';
  messageId: string;
  content?: string;
};

type AssistantMessageEvent = AssistantMessageStart | AssistantMessageDelta;

export function isToolCallEvent(event: any): event is ToolCallEvent {
  if (typeof event !== 'object' || event === null) return false;
  return 'type' in event && event.type === 'tool_call' && 'callId' in event;
}

export function isStatusEvent(event: any): event is StatusEvent {
  if (typeof event !== 'object' || event === null) return false;
  return 'type' in event && event.type === 'status';
}

function reduceMessagesByWsEvent(
  allMessages: ChatMessage[],
  payload: ToolCallEvent | AssistantMessageEvent | StatusEvent
): ChatMessage[] {
  if (isStatusEvent(payload)) {
    if (payload.event === 'error' && payload.messageId && payload.message) {
      const messageExists = allMessages.some((message) => message.id === payload.messageId);

      if (messageExists) return allMessages;

      const errorMessage: AssistantMessage = {
        id: payload.messageId,
        role: 'assistant',
        text: payload.message,
        errorMessage: payload.message,
        errorType: payload.errorType,
      };

      return [...allMessages, errorMessage];
    }

    return allMessages;
  }

  if (isToolCallEvent(payload)) {
    const toolPayload = payload;
    const { event: toolCallEvent, callId: toolCallId, name: toolCallName } = toolPayload;

    const baseToolMessage: ToolMessage = {
      id: toolCallId,
      role: 'tool',
      callId: toolCallId,
      toolName: toolCallName,
      toolLabel: toolPayload.label,
      toolDescription: toolPayload.description,
      status: 'in_progress',
    };

    if (toolCallEvent === 'start') {
      const toolCallExists = allMessages.some(
        (message) => message.role === 'tool' && message.callId === toolCallId
      );
      if (toolCallExists) return allMessages;

      return [...allMessages, baseToolMessage];
    }

    if (toolCallEvent === 'delta') {
      const { content: toolCallContent } = toolPayload;
      if (!toolCallContent) return allMessages;

      const toolCallIndex = allMessages.findIndex(
        (message) => message.role === 'tool' && message.callId === toolCallId
      );

      if (toolCallIndex === -1) {
        return [...allMessages, baseToolMessage];
      }

      return allMessages;
    }

    if (toolCallEvent === 'end') {
      const { hasError, label, description, errorType, content } = toolPayload;

      const patch = {
        status: hasError ? 'error' : 'done',
        errorType: hasError ? errorType : undefined,
        errorMessage: hasError ? content : undefined,
        toolLabel: label,
        toolDescription: description,
      } as const;

      const toolCallIndex = allMessages.findIndex(
        (m) => m.role === 'tool' && m.callId === toolCallId
      );

      if (toolCallIndex === -1) {
        return [...allMessages, { ...baseToolMessage, ...patch }];
      }

      return allMessages.map((message, idx) =>
        idx === toolCallIndex ? { ...message, ...patch } : message
      );
    }
  }

  const {
    event: assistantMessageEvent,
    messageId: assistantMessageId,
    content: assistantMessageContent,
  } = payload as AssistantMessageEvent;

  if (assistantMessageEvent === 'start') {
    const messageExists = allMessages.some((message) => message.id === assistantMessageId);

    if (messageExists) return allMessages;

    const initialText = assistantMessageContent ?? '';
    const newMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      text: initialText,
    };
    return [...allMessages, newMessage];
  }

  if (assistantMessageEvent === 'delta') {
    if (!assistantMessageId || !assistantMessageContent) {
      return allMessages;
    }

    return allMessages.map((message) =>
      message.id === assistantMessageId && message.role === 'assistant'
        ? { ...message, text: `${message.text}${assistantMessageContent}` }
        : message
    );
  }

  return allMessages;
}

type UseAIBuilderChatIdOptions = {
  journeyShortId: string;
};

export const useAIBuilderChat = ({ journeyShortId }: UseAIBuilderChatIdOptions) => {
  const [storedChatId, setChatId] = useLocalStorageState<string | null>({
    key: `ai-builder-chat-${journeyShortId}`,
    defaultValue: null,
  });

  const chatId =
    storedChatId ||
    (() => {
      const newChatId = uuid();
      setChatId(newChatId);
      return newChatId;
    })();

  const { data: messagesData } = useAIBuilderMessages(journeyShortId, chatId);

  const [isGenerating, setIsGenerating] = useLocalStorageState<boolean>({
    key: `ai-builder-generating-${chatId}`,
    defaultValue: false,
  });

  const clearChat = useCallback(() => {
    setIsGenerating(false);
    setChatId(null);
  }, [setChatId, setIsGenerating]);

  const reduceByWsEvent = useCallback(
    (allMessages: ChatMessage[], payload: ToolCallEvent | AssistantMessageEvent | StatusEvent) =>
      reduceMessagesByWsEvent(allMessages, payload),
    []
  );

  const startRequest = useCallback(() => {
    setIsGenerating(true);
  }, [setIsGenerating]);

  const endRequest = useCallback(() => {
    setIsGenerating(false);
  }, [setIsGenerating]);

  return {
    chatId,
    messages: messagesData?.messages ?? [],
    isGenerating,
    setChatId,
    clearChat,
    reduceByWsEvent,
    startRequest,
    endRequest,
  };
};
